import { zodResolver } from '@hookform/resolvers/zod';
import { InboxMessage } from '@src/main/modules/messages/domain/message/inbox/inbox-message.interface';
import { getNewMessageSchema } from '@src/main/modules/messages/domain/message/new-message.schema';
import {
  CreateMessageRequest,
  RecipientCreateRequest,
} from '@src/main/modules/messages/infrastructure/dto/createMessageRequest';
import { NewMessageFormData } from '@src/main/modules/messages/ui/mailbox/components/new-message/types/NewMessageFormData';
import {
  GroupSC,
  GroupSCLabels,
  RecipientType,
} from '@src/main/modules/messages/ui/mailbox/components/new-message/types/recipient.types';
import { useFindInboxMessageById } from '@src/main/modules/messages/ui/mailbox/hooks/mailbox/useFindInboxMessageById';
import { useCreateMessage } from '@src/main/modules/messages/ui/mailbox/hooks/new-message/useCreateMessage';
import { useCrewSearch } from '@src/main/modules/messages/ui/mailbox/hooks/new-message/useCrewSearch';
import { useFleetOptions } from '@src/main/modules/messages/ui/mailbox/hooks/new-message/useFleetOptions';
import { SCOption } from '@src/main/modules/messages/ui/mailbox/model/crew-option.model';
import { useToast } from '@src/main/shared/context/ToastContext';
import { showErrorMessage } from '@src/main/shared/utils/toastUtils';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const getRecipients = (
  formData: NewMessageFormData,
  originalMessage?: InboxMessage,
): RecipientCreateRequest[] => {
  if (originalMessage) {
    return [{ recipient: originalMessage.sendBy }];
  }

  switch (formData.recipients) {
    case RecipientType.SUBFLEET:
      return (formData.fleets ?? []).map((fleetObj) => ({
        recipient: `${fleetObj.fleet} (${fleetObj.subFleet})`,
      }));
    case RecipientType.PLATE:
      return (formData.numberPlates ?? []).map((plate: string) => ({
        recipient: plate,
      }));
    case RecipientType.SC_LIST:
      return (formData.scs ?? []).map(
        (sc: { payrollNumber: string; name: string; surname: string }) => ({
          recipient: sc.payrollNumber,
          recipientName: `${sc.name} ${sc.surname}`,
        }),
      );
    case RecipientType.SC_GROUP:
      return (formData.gsc ?? []).map(
        (sc: string): RecipientCreateRequest => ({
          recipient: sc,
        }),
      );
    default:
      return [];
  }
};

export const useNewMessageForm = (messageId?: string) => {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const schema = getNewMessageSchema(t);

  const [scQuery, setScQuery] = useState('');

  const { data: originalMessage, isLoading: isLoadingOriginalMessage } = useFindInboxMessageById(
    messageId ?? '',
    { enabled: !!messageId },
  );

  const { data: rawSCOptions = [] } = useCrewSearch(scQuery.length >= 3 ? scQuery : '');

  const scOptions: SCOption[] = useMemo(
    () =>
      rawSCOptions.map((item) => ({
        ...item,
        surname: item.surnames,
        label: `${item.name} ${item.surnames} (${item.payrollNumber})`,
      })),
    [rawSCOptions],
  );

  const { options: fleetOptions = [] } = useFleetOptions('*', '*');

  const groupSCOptions = useMemo(
    () =>
      Object.values(GroupSC).map((group) => ({
        name: t(GroupSCLabels[group]),
        value: group,
      })),
    [t],
  );

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { isSubmitting },
  } = useForm<NewMessageFormData>({
    defaultValues: {
      recipients: RecipientType.SUBFLEET,
      subject: originalMessage ? `RE: ${originalMessage.subject}` : '',
      messageText: '',
      messageHtml: '',
      fleets: [],
      numberPlates: [],
      scs: [],
      gsc: [],
      initDate: undefined,
      endDate: undefined,
      attachment: undefined,
    },
    resolver: zodResolver(schema),
    shouldUnregister: true,
  });

  const { mutateAsync: createMessage } = useCreateMessage();

  const onSubmit = async (formData: NewMessageFormData) => {
    try {
      const recipients = getRecipients(formData, originalMessage);

      const payload: CreateMessageRequest = {
        subject: formData.subject,
        message: formData.messageText,
        messageHtml: formData.messageHtml,
        recipients,
        attachment: formData.file?.name,
        file: formData.file ?? undefined,
        respondedFrom: originalMessage?.messageId,
        initDate: formData.initDate || undefined,
        endDate: formData.endDate || undefined,
      };

      await createMessage(payload);
      navigate('/messages/mailbox');
    } catch (err) {
      showErrorMessage(toast, err);
    }
  };

  const recipientType = watch('recipients');
  const initDate = watch('initDate');

  return {
    control,
    handleSubmit,
    onSubmit,
    watch,
    setValue,
    originalMessage,
    isLoadingOriginalMessage,
    recipientType,
    scQuery,
    setScQuery,
    scOptions,
    fleetOptions,
    groupSCOptions,
    initDate,
    isSubmitting,
  };
};
